import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {first} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {SegmentCustomEvent} from '@ionic/angular';
import {Operatore, Organizzazione} from '../_models/_Index';
import {OrdiniStat} from '../_models/OrdiniStat';
import {EventiCounter} from '../_models/EventiCounter';
import {RisorseCounter} from '../_models/RisorseCounter';
import {QuantitaUdM} from '../_models/QuantitaUdM';
import {QualitaStatisticheStat} from '../_models/QualitaStatisticheStat';
import {OrdiniVenditaStat} from '../_models/OrdiniVenditaStat';
import {SinotticoStato} from '../sinottico/_models/SinotticoStato';
import {FILTRO_VUOTO} from '../sinottico/_models/SinotticoFiltro';
import {contatoriPerStato, queryStringDaFiltro} from '../sinottico/_services/sinottico-filtro.helper';
import {SinotticoService} from '../sinottico/_services/sinottico.service';
import {VenditaService} from '../_services/vendita.service';
import {AuthenticationService} from '../_services/authentication.service';
import {OrganizzazioniService} from '../_services/organizzazioni.service';
import {LicenzeService} from '../_services/licenze.service';
import {MenuAccessService} from '../_services/menu-access.service';
import {StatisticheService} from '../_services/statistiche.service';
import {SaveMessage} from '../_helpers/save-message';
import {constants} from '../_helpers/constants';
import {
  finestra, giornoQueryParam, isPeriodoDashboard, PERIODI_DASHBOARD, PeriodoDashboard, variazionePercentuale
} from '../_helpers/dashboard-periodo';

const CHIAVE_PERIODO = 'dashboard.periodo';

/** Preferenza del singolo utente (research R10): lo storage può mancare o lanciare, si ripiega su «Oggi». */
function leggiPeriodo(): PeriodoDashboard {
  try {
    const valore = localStorage.getItem(CHIAVE_PERIODO);
    return isPeriodoDashboard(valore) ? valore : 'oggi';
  } catch {
    return 'oggi';
  }
}

function salvaPeriodo(periodo: PeriodoDashboard) {
  try {
    localStorage.setItem(CHIAVE_PERIODO, periodo);
  } catch {
    // preferenza non salvata: al prossimo accesso torna «Oggi»
  }
}

export interface WidgetVisibili {
  statoMacchine: boolean;
  qualita: boolean;
  ordiniClienti: boolean;
}

@Component({
  selector: 'app-dashboard-home',
  templateUrl: './dashboard-home.component.html',
  styleUrls: ['./dashboard-home.component.scss'],
  standalone: false
})
export class DashboardHomeComponent implements OnInit, OnDestroy {

  userName = '';
  firstName = '';
  organizzazioneCodice = '';
  isStandalone = false;
  constants = constants;

  now: Date = new Date();
  loading = false;
  loadError = false;
  ordiniStat?: OrdiniStat;

  /** Tri-state (issue #3169): null = caricamento (nessun flash), false = nessuna org attiva → avviso. */
  hasActiveOrgs: boolean | null = null;

  /** Mappa di accesso alle quick actions. Pre-calcolata e ricalcolata a ogni cambio org/utente. */
  quickActionsAccess: { [key: string]: boolean } = {};

  /** Feature 161: periodo dei KPI e della ciambella. */
  periodo: PeriodoDashboard = leggiPeriodo();
  readonly periodi = PERIODI_DASHBOARD;
  /** Giorni mostrati dalle barre (gli eventi arrivano sempre per l'ultimo mese). */
  giorniGrafico: 7 | 30 = 30;

  widgetVisibili: WidgetVisibili = {statoMacchine: false, qualita: false, ordiniClienti: false};
  contatoriStato: Record<SinotticoStato, number> | null = null;
  erroreStato = false;
  qualitaStat: QualitaStatisticheStat | null = null;
  erroreQualita = false;
  ordiniClientiStat: OrdiniVenditaStat | null = null;
  erroreOrdiniClienti = false;

  // Sempre lo stesso array vuoto: un `?? []` nel template ne crea uno nuovo a ogni change detection, i grafici lo
  // vedono come input cambiato e rilanciano il disegno (e un'altra change detection) finché i dati non arrivano.
  readonly nessunEvento: EventiCounter[] = [];
  readonly nessunaRisorsa: RisorseCounter[] = [];

  readonly barChartColors = ['#fbbf24', '#a78bfa', '#22c55e', '#94a3b8'];
  readonly pieChartColors = ['#fbbf24', '#22c55e', '#3b82f6', '#a78bfa', '#ef4444', '#06b6d4', '#f97316', '#ec4899'];

  private subscription = new Subscription();
  /** Incrementata a ogni aggiornamento completo (cambio organizzazione, «Aggiorna»): scarta le risposte vecchie. */
  private generazione = 0;
  /** Generazione per cui ogni widget è già stato chiesto: evita richieste doppie quando i permessi ri-emettono. */
  private widgetChiesti: Record<keyof WidgetVisibili, number> = {statoMacchine: -1, qualita: -1, ordiniClienti: -1};
  private richiestaStatistiche = 0;
  private clockHandle: any;

  constructor(
    public authService: AuthenticationService,
    public licenseService: LicenzeService,
    private menuAccess: MenuAccessService,
    private route: ActivatedRoute,
    private organizzazioniService: OrganizzazioniService,
    private statisticheService: StatisticheService,
    private saveMessage: SaveMessage,
    private sinotticoService: SinotticoService,
    private venditaService: VenditaService,
    private router: Router) { }

  get greeting(): string {
    const h = this.now.getHours();
    if (h < 13) return 'Buongiorno';
    if (h < 19) return 'Buon pomeriggio';
    return 'Buonasera';
  }

  get ordiniLink(): string[] {
    return ['/ordini', 'inviate'];
  }

  get etichettaPeriodo(): string {
    return this.periodi.find(p => p.valore === this.periodo)?.etichetta ?? '';
  }

  get quantitaPrincipale(): QuantitaUdM | null {
    return this.ordiniStat?.quantitaTerminataPerUdM?.[0] ?? null;
  }

  get altreQuantita(): QuantitaUdM[] {
    return this.ordiniStat?.quantitaTerminataPerUdM?.slice(1) ?? [];
  }

  /** «+12%», «-5%» o «—» quando il periodo precedente non ha produzione. */
  get variazioneProdotto(): string {
    const q = this.quantitaPrincipale;
    const variazione = q ? variazionePercentuale(q.quantita, q.quantitaPrecedente) : null;
    return variazione === null ? '—' : `${variazione > 0 ? '+' : ''}${variazione}%`;
  }

  get puoAprireEventi(): boolean {
    return this.menuAccess.can('analisi-eventi');
  }

  get puoAprireSinottico(): boolean {
    return this.menuAccess.can('analisi-sinottico');
  }

  get hasChartData(): boolean {
    return (this.ordiniStat?.eventiLastMonth?.length ?? 0) > 0
      || (this.ordiniStat?.risorseOfToday?.length ?? 0) > 0;
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params.id_token == null) {
        return false;
      }
      this.authService.externalLogin(params.id_token, params.refresh_token)
        .pipe(first())
        .subscribe(
          data => {
            if (data && data.organizzazioneId) {
              void Promise.all([
                this.organizzazioniService.setCurrentOrganizzazioneValueById(data.organizzazioneId),
                this.licenseService.ensureModuliOfOrgLoadedAsync(data.organizzazioneId)
              ]).catch(() => {
                this.saveMessage.messageError('Errore nel caricamento di organizzazione e licenze.');
              });
            }
          }
        );
    });

    if (this.authService.currentUserValue != null) {
      this.userName = this.authService.currentUserValue.userName;
      this.firstName = this.authService.currentUserValue.firstName ?? this.authService.currentUserValue.userName;
    }

    if (this.authService.currentUserValue?.jwtToken) {
      this.checkActiveOrgs();
    }

    this.subscription.add(this.authService.currentUser.subscribe((_user: Operatore) => {
      if (_user != null) {
        this.userName = (_user.firstName ?? '') + ' ' + (_user.lastName ?? '');
        this.firstName = _user.firstName ?? _user.userName ?? '';
      } else {
        this.userName = '';
        this.firstName = '';
      }
    }));

    this.subscription.add(this.organizzazioniService.currentOrganizzazione.subscribe((_org: Organizzazione) => {
      if (_org) {
        this.organizzazioneCodice = _org.descrizione;
      }
      this.isStandalone = this.organizzazioniService.isStandalone();
      this.computeQuickActionsAccess();
      this.refresh();
      if (this.authService.currentUserValue?.jwtToken) {
        this.checkActiveOrgs();
      }
    }));

    // Ricalcola lo stato delle quick actions ogni volta che cambiano i permessi caricati.
    // Necessario perché al refresh della pagina il subscribe a currentOrganizzazione può
    // scattare PRIMA che le licenze siano state caricate, lasciando tutte le card disabilitate.
    this.subscription.add(this.licenseService.currentPermessi$.subscribe(() => {
      this.computeQuickActionsAccess();
      this.caricaWidgetDivenutiVisibili();
    }));

    this.clockHandle = setInterval(() => this.now = new Date(), 1000);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.clockHandle) {
      clearInterval(this.clockHandle);
    }
  }

  refresh() {
    if (!this.authService.currentUserValue?.jwtToken) {
      this.loading = false;
      this.loadError = false;
      return;
    }

    const orgId = this.organizzazioniService.organizzazioneId;
    if (!orgId) {
      return;
    }
    this.generazione++;
    this.caricaStatistiche(orgId);
    this.caricaWidget(orgId);
  }

  cambiaPeriodo(event: SegmentCustomEvent) {
    const valore = event.detail.value;
    if (!isPeriodoDashboard(valore) || valore === this.periodo) {
      return;
    }
    this.periodo = valore;
    salvaPeriodo(valore);
    const orgId = this.organizzazioniService.organizzazioneId;
    if (orgId && this.authService.currentUserValue?.jwtToken) {
      this.caricaStatistiche(orgId);
    }
  }

  cambiaGiorniGrafico(event: SegmentCustomEvent) {
    this.giorniGrafico = event.detail.value === '7' ? 7 : 30;
  }

  apriGiorno(giorno: EventiCounter) {
    if (this.puoAprireEventi) {
      void this.router.navigate(['/eventi'], {queryParams: {giorno: giornoQueryParam(giorno.day)}});
    }
  }

  apriRisorsa(risorsa: RisorseCounter) {
    if (this.puoAprireSinottico) {
      const ricerca = risorsa.risorsaCodice || String(risorsa.risorsaId);
      void this.router.navigate(['/sinottico'], {queryParams: queryStringDaFiltro({...FILTRO_VUOTO, ricerca})});
    }
  }

  /** Una sola chiamata con `giornoLocale`: KPI, barre e ciambella. Solo il suo errore accende il badge ERRORE. */
  private caricaStatistiche(orgId: number) {
    const richiesta = ++this.richiestaStatistiche;
    const attuale = () => richiesta === this.richiestaStatistiche;
    const {start, end} = finestra(this.periodo, new Date());
    this.loading = true;
    this.loadError = false;
    this.statisticheService.getStatistics(start, end, orgId, -1, true)
      .then((ordini) => { if (attuale()) this.ordiniStat = ordini; })
      .catch(() => {
        if (!attuale()) return;
        this.loadError = true;
        this.saveMessage.messageError('Ci sono problemi con il caricamento delle statistiche.');
      })
      .finally(() => { if (attuale()) this.loading = false; });
  }

  /** Solo i widget visibili, in parallelo; ognuno gestisce il proprio errore. */
  private caricaWidget(orgId: number) {
    if (this.widgetVisibili.statoMacchine) this.caricaStatoMacchine(orgId);
    if (this.widgetVisibili.qualita) this.caricaQualita(orgId);
    if (this.widgetVisibili.ordiniClienti) this.caricaOrdiniClienti(orgId);
  }

  /** Permessi arrivati dopo il caricamento: si caricano i widget appena diventati visibili. */
  private caricaWidgetDivenutiVisibili() {
    const orgId = this.organizzazioniService.organizzazioneId;
    if (!orgId || !this.authService.currentUserValue?.jwtToken) return;
    const daChiedere = (w: keyof WidgetVisibili) =>
      this.widgetVisibili[w] && this.widgetChiesti[w] !== this.generazione;
    if (daChiedere('statoMacchine')) this.caricaStatoMacchine(orgId);
    if (daChiedere('qualita')) this.caricaQualita(orgId);
    if (daChiedere('ordiniClienti')) this.caricaOrdiniClienti(orgId);
  }

  private caricaStatoMacchine(orgId: number) {
    const generazione = this.widgetChiesti.statoMacchine = this.generazione;
    this.contatoriStato = null;
    this.erroreStato = false;
    this.sinotticoService.getStatoRisorse(this.sinotticoService.richiestaPer(orgId))
      .then(risorse => { if (generazione === this.generazione) this.contatoriStato = contatoriPerStato(risorse); })
      .catch(() => { if (generazione === this.generazione) this.erroreStato = true; });
  }

  private caricaQualita(orgId: number) {
    const generazione = this.widgetChiesti.qualita = this.generazione;
    const {start, end} = finestra('30', new Date());
    this.qualitaStat = null;
    this.erroreQualita = false;
    this.statisticheService.getQualitaStatistiche(start, end, orgId)
      .then(stat => { if (generazione === this.generazione) this.qualitaStat = stat; })
      .catch(() => { if (generazione === this.generazione) this.erroreQualita = true; });
  }

  private caricaOrdiniClienti(orgId: number) {
    const generazione = this.widgetChiesti.ordiniClienti = this.generazione;
    this.ordiniClientiStat = null;
    this.erroreOrdiniClienti = false;
    this.venditaService.getOrdiniVenditaStatAsync(orgId, undefined, {})
      .then(stat => { if (generazione === this.generazione) this.ordiniClientiStat = stat; })
      .catch(() => { if (generazione === this.generazione) this.erroreOrdiniClienti = true; });
  }

  /** Verifica se l'operatore ha almeno un'organizzazione attiva (issue #3169). Tri-state per evitare flash. */
  private checkActiveOrgs() {
    this.organizzazioniService.getOrganizzazioniFilteredAsync('', 0, 0, 'OrgName', true)
      .then((result) => this.hasActiveOrgs = (result?.result?.length ?? 0) > 0)
      .catch(() => this.hasActiveOrgs = null);
  }

  /**
   * Calcola la matrice di accesso per ogni quick action della dashboard, dalla stessa fonte che
   * governa il menù laterale: MenuAccessService.can(id), non più una copia locale di modulo/ruoli.
   * Un binding cambiato in Mappatura menù (o la rifinitura per organizzazione) si riflette qui
   * senza bisogno di toccare questo file.
   */
  private computeQuickActionsAccess() {
    const m = this.menuAccess;
    this.quickActionsAccess = {
      avanzamento: m.can('mes-avanzamento'),
      trasferimento: m.can('mes-trasferimento'),
      ordini: m.can('ordini-inviati'),
      ricevimento: m.can('ricevimenti-ddt'),
      terzista: m.can('conto-lavoro-invio'),
      ddtTerzisti: m.can('conto-lavoro-ddt'),
      spedizione: m.can('spedizioni-imballaggio'),
      ordiniClienti: m.can('vendita-ordini-clienti'),
      anagrafiche: m.can('gestione-anagrafiche'),
      risorse: m.can('gestione-risorse'),
      lavorazioni: m.can('gestione-lavorazioni'),
      movimenti: m.can('analisi-movimenti'),
      contenitori: m.can('gestione-contenitori'),
      qualita: m.can('qualita-controlli'),
      pianificazione: m.can('pianificazione-consegne'),
      articoli: m.can('analisi-articoli'),
      eventi: m.can('analisi-eventi'),
      sessioni: m.can('analisi-sessioni'),
      inventario: m.can('inventario-manuale'),
      pesate: m.can('analisi-pesate')
    };
    this.widgetVisibili = {
      statoMacchine: m.can('analisi-sinottico'),
      qualita: m.can('qualita-controlli'),
      ordiniClienti: m.can('vendita-ordini-clienti')
    };
  }
}
